import { useState, useEffect } from 'react'
import { useMediaSession } from '../hooks/useMediaSession'
import { useMediaPermissions } from '../hooks/useMediaPermissions'
import MediaDock from './MediaDock'
import MediaPlayer from './MediaPlayer'
import MediaControls from './MediaControls'
import MediaQueue from './MediaQueue'
import MediaChannelChat from './MediaChannelChat'
import { fetchYouTubeMetadata, extractYouTubeId } from '../utils/youtubeHelper'
import { MessageCircle, X } from 'lucide-react'

/**
 * MediaChannel - Complete Media Watch Party Interface
 *
 * Integrates all media components:
 * - MediaDock (drag & drop)
 * - MediaPlayer (synced playback)
 * - MediaControls (admin controls)
 * - MediaQueue (queue management)
 *
 * Manages permissions and real-time sync via hooks
 */
export default function MediaChannel({ channel, space, user, profile }) {
  const [showQueue, setShowQueue] = useState(false)
  const [showChat, setShowChat] = useState(true) // Chat visible by default

  // Get media permissions
  const { permissions, loading: permissionsLoading } = useMediaPermissions({
    spaceId: space.id,
    userId: user.id,
    channelId: channel.id
  })

  // Get media session and queue
  const {
    session,
    queue,
    loading: sessionLoading,
    error: sessionError,
    createSession,
    play,
    pause,
    seek,
    addToQueue,
    removeFromQueue,
    playNext,
    clearQueue,
    updatePlayback,
    takeControl,
    endSession
  } = useMediaSession({
    spaceId: space.id,
    userId: user.id
  })

  // Auto-take control if user is admin and no leader
  useEffect(() => {
    if (session && !session.leader_id && permissions.canControl) {
      takeControl()
    }
  }, [session, permissions.canControl])

  // Handle media added from dock
  const handleMediaAdd = async (mediaData) => {
    try {
      const { type, url, title, thumbnail, duration, file } = mediaData

      // If file upload, upload to Supabase Storage first
      let finalUrl = url
      if (file) {
        // TODO: Upload to Supabase Storage bucket: media-uploads
        // For now, use object URL
        console.log('File upload not yet implemented. Using object URL.')
        finalUrl = url
      }

      // Fetch additional metadata for YouTube videos
      let finalTitle = title
      let finalThumbnail = thumbnail
      let finalDuration = duration
      let videoId = null

      if (type === 'youtube') {
        videoId = extractYouTubeId(url)
        if (videoId) {
          const metadata = await fetchYouTubeMetadata(videoId)
          if (metadata) {
            finalTitle = metadata.title
            finalThumbnail = metadata.thumbnail
            finalDuration = metadata.duration
          }
        }
      }

      // If no session exists, create one
      // Signature: createSession(platform, videoId, videoUrl, title, thumbnail, duration)
      if (!session) {
        await createSession(
          type,
          videoId || finalUrl, // Use videoId for YouTube, URL for others
          finalUrl,
          finalTitle,
          finalThumbnail,
          finalDuration
        )
      } else {
        // Add to queue
        // Signature: addToQueue(platform, videoId, videoUrl, title, thumbnail, duration)
        await addToQueue(
          type,
          videoId || finalUrl,
          finalUrl,
          finalTitle,
          finalThumbnail,
          finalDuration
        )
      }
    } catch (error) {
      console.error('Error adding media:', error)
      alert('Failed to add media. Please try again.')
    }
  }

  // Handle play
  const handlePlay = async () => {
    try {
      await play()
    } catch (error) {
      console.error('Error playing:', error)
    }
  }

  // Handle pause
  const handlePause = async () => {
    try {
      await pause()
    } catch (error) {
      console.error('Error pausing:', error)
    }
  }

  // Handle seek
  const handleSeek = async (time) => {
    try {
      await seek(time)
    } catch (error) {
      console.error('Error seeking:', error)
    }
  }

  // Handle previous (for now, just a placeholder)
  const handlePrevious = () => {
    console.log('Previous not yet implemented')
    // TODO: Implement previous history
  }

  // Handle next (play next in queue)
  const handleNext = async () => {
    try {
      await playNext()
    } catch (error) {
      console.error('Error playing next:', error)
    }
  }

  // Handle media ended (auto-play next)
  const handleMediaEnded = async () => {
    if (permissions.canControl) {
      await handleNext()
    }
  }

  // Handle fullscreen toggle
  const handleToggleFullscreen = () => {
    // TODO: Implement fullscreen mode
    console.log('Fullscreen not yet implemented')
  }

  // Handle queue toggle
  const handleToggleQueue = () => {
    setShowQueue(!showQueue)
  }

  // Handle remove from queue
  const handleRemoveFromQueue = async (itemId) => {
    try {
      await removeFromQueue(itemId)
    } catch (error) {
      console.error('Error removing from queue:', error)
    }
  }

  // Handle queue reorder
  const handleReorder = async (itemId, newPosition) => {
    try {
      // TODO: Implement reordering logic
      // For now, log it
      console.log('Reorder:', itemId, 'to position', newPosition)
    } catch (error) {
      console.error('Error reordering queue:', error)
    }
  }

  // Handle clear queue
  const handleClearQueue = async () => {
    if (confirm('Clear entire queue?')) {
      try {
        await clearQueue()
      } catch (error) {
        console.error('Error clearing queue:', error)
      }
    }
  }

  // Loading state
  if (permissionsLoading || sessionLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gjyl-slate-900">
        <div className="text-center space-y-4">
          <div className="text-6xl animate-pulse">🎬</div>
          <p className="text-slate-400">Loading media channel...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (sessionError) {
    return (
      <div className="h-full flex items-center justify-center bg-gjyl-slate-900">
        <div className="text-center space-y-4">
          <div className="text-6xl">⚠️</div>
          <p className="text-red-400">Error loading media session</p>
          <p className="text-slate-500 text-sm">{sessionError}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 h-full flex flex-col lg:flex-row gap-4 p-4 pl-6 bg-gjyl-darker overflow-hidden">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col gap-3 min-w-0 min-h-0 overflow-hidden max-w-4xl">
        {/* Media Player - constrained size */}
        {session ? (
          <div className="flex-shrink-0 max-w-2xl">
            <MediaPlayer
              session={session}
              onTimeUpdate={(data) => {
                // Update session playback_position periodically
                if (permissions.canControl && session.state === 'playing') {
                  updatePlayback({
                    playback_position: data.currentTime,
                    duration: data.duration
                  })
                }
              }}
              onEnded={handleMediaEnded}
              canControl={permissions.canControl}
            />
          </div>
        ) : null}

        {/* Admin Controls or Regular User View */}
        {session && (
          <div className="glass-strong rounded-xl p-6">
            {permissions.canControl ? (
              <MediaControls
                session={session}
                onPlay={handlePlay}
                onPause={handlePause}
                onSeek={handleSeek}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onToggleFullscreen={handleToggleFullscreen}
                onToggleQueue={handleToggleQueue}
                queueCount={queue.length}
                showQueue={showQueue}
              />
            ) : (
              // Regular user view (read-only)
              <div className="text-center space-y-3">
                <div className="h-2 bg-gjyl-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gjyl-indigo-500 transition-all duration-300"
                    style={{
                      width: `${session.duration ? ((session.playback_position || 0) / session.duration) * 100 : 0}%`
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">
                    {Math.floor((session.playback_position || 0) / 60)}:{String(Math.floor((session.playback_position || 0) % 60)).padStart(2, '0')}
                  </span>
                  <span className="text-slate-400">
                    {session.duration ? `${Math.floor(session.duration / 60)}:${String(Math.floor(session.duration % 60)).padStart(2, '0')}` : '--:--'}
                  </span>
                </div>
                {session.leader && (
                  <p className="text-sm text-slate-400 mt-4">
                    Controlled by:{' '}
                    <span className="text-white font-medium">
                      @{session.leader.username}
                    </span>
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Media Dock - Only show when no session active */}
        {!session && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <MediaDock
              onMediaAdd={handleMediaAdd}
              disabled={!permissions.canControl}
            />
          </div>
        )}
      </div>

      {/* Right Side Panel: Chat or Queue */}
      <div className="w-full lg:w-80 xl:w-96 flex-shrink-0 flex flex-col gap-3">
        {/* Toggle Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowChat(!showChat)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all
              ${showChat
                ? 'bg-indigo-600/20 text-indigo-400 border border-indigo-500/30'
                : 'bg-gray-800/40 text-gray-400 border border-gray-700/30 hover:bg-gray-700/40 hover:text-white'
              }
            `}
          >
            <MessageCircle className="w-4 h-4" />
            Chat
          </button>
          {showQueue && (
            <button
              onClick={() => setShowQueue(false)}
              className="p-2 bg-gray-800/40 text-gray-400 border border-gray-700/30 rounded-xl hover:bg-gray-700/40 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="flex-1 min-h-0 lg:h-[calc(100vh-12rem)]">
            <MediaChannelChat
              channel={channel}
              user={user}
              profile={profile}
            />
          </div>
        )}

        {/* Queue Panel (overlays chat when open) */}
        {showQueue && (
          <div className="absolute right-4 top-20 z-10">
            <MediaQueue
              queue={queue}
              onRemove={handleRemoveFromQueue}
              onReorder={handleReorder}
              onClearAll={handleClearQueue}
              canControl={permissions.canControl}
              onClose={() => setShowQueue(false)}
            />
          </div>
        )}
      </div>
    </div>
  )
}
